import { Icon, IconLike } from "components/Icon/Icon";
import { parseIconValue, resolveCuratedIcon } from "components/Icon/iconValue";
import { singleCustomObjectProvisioningStateSelector } from "components/ProvisioningStateDot/ProvisioningState.selectors";
import { FeRecordType } from "config/config";
import { getBundleData } from "containers/Bundles/Bundles.selectors";
import { getProjectData } from "containers/Projects/Projects.selectors";
import { useMemo } from "react";
import { shallowEqual, useSelector } from "react-redux";
import "./ImageIcon.scss";

interface ImageIconProps {
    image?: string | null;
    icon?: IconLike;
    className?: string;
    iconByType?: FeRecordType;
    imageComponent?: React.ReactElement;
}

export const imageProps = (
    type: FeRecordType,
    id: string | null,
    objectTemplateId?: string | null
) => {
    const haveImage = id && (type === "custom_object" || type === "project" || type === "bundle");

    if (haveImage) {
        return { imageComponent: <ImageComponent type={type} id={id} objectTemplateId={objectTemplateId} /> };
    }
    return {};
};

const selectProjectIcon = (id) => (state) => {
    const project = getProjectData(state, id);
    return project ? project.icon : null;
};

const selectBundleIcon = (id) => (state) => {
    const bundle = getBundleData(state, id);
    return bundle ? bundle.icon : null;
};

// A custom object displays its OBJECT TEMPLATE's icon. Resolve it from the
// complete, route-independent `objectTemplateSchema` store (pushed app-wide on
// the live `/ui` websocket, keyed by template id, carrying each template's
// `icon`) whenever the template id is known from the row. Fall back to the
// status-dot provisioning-state cache (`object_template_id_object_template.icon`)
// when the template id is NOT available at the call site: that cache only holds
// non-READY custom objects since the status-dot channel was narrowed
// (51b9137ef), so the primary path must be the template-schema lookup — relying
// on the provisioning cache alone leaves every READY custom object with no icon.
const selectCustomObjectIcon = (id, objectTemplateId) => (state) => {
    if (objectTemplateId) {
        // Read the `objectTemplateSchema` store slice directly rather than
        // importing it from `containers/Resources/Resources.selectors`: that
        // module transitively pulls `ResourcesSingle/utils` and the heavy
        // `config/resources` graph into this leaf component's module init,
        // which creates an import cycle (eager `checkSSPHidden()` in
        // `config/resources/workspace.tsx` resolving undefined at load).
        const objectTemplateSchema = state?.dataStore?.objectTemplateSchema ?? [];
        const template = objectTemplateSchema.find(
            (entry) => entry?.id === objectTemplateId
        );
        if (template) {
            return template.icon || null;
        }
    }
    const provisioningStateData = singleCustomObjectProvisioningStateSelector(id)(state);
    return provisioningStateData?.object_template_id_object_template?.icon || null;
};

export const getIconSelector = (
    type: FeRecordType,
    id: string,
    objectTemplateId?: string | null
) => {
    switch (type) {
        case "custom_object":
            return selectCustomObjectIcon(id, objectTemplateId);
        case "project":
            return selectProjectIcon(id);
        case "bundle":
            return selectBundleIcon(id);
        default:
            return () => null;
    }
};

const ImageComponent = ({
    type,
    id,
    objectTemplateId,
}: {
    type?: FeRecordType;
    id?: string | null;
    objectTemplateId?: string | null;
}) => {
    const iconSelector = useMemo(
        () => (type && id ? getIconSelector(type, id, objectTemplateId) : () => null),
        [id, type, objectTemplateId]
    );
    const image = useSelector(iconSelector, shallowEqual);

    if (!id || !type) {
        return null;
    }

    return <ImageIconComponent image={image} iconByType={type} />;
};

export const ImageIconComponent = ({
    image,
    icon,
    className,
    iconByType,
    imageComponent,
}: ImageIconProps) => {
    if (!icon && !image && !iconByType && !imageComponent) {
        return null;
    }

    if (imageComponent) {
        return imageComponent;
    }

    // `image` is the record's stored `icon` value, which may be either an
    // uploaded image (`data:` URI → bitmap) or a curated glyph reference
    // (`mdi:<key>` → themed vector). An explicit `icon` component prop, when
    // provided, always wins; otherwise a curated reference beats the
    // record-type default (`iconByType`), and an unknown/absent value falls
    // through to the type default.
    const parsed = parseIconValue(image);
    if (parsed.kind === "image") {
        return (
            <span
                style={{ backgroundImage: `url(${parsed.value})` }}
                className={`imageIcon image ${className}`}
            />
        );
    }

    const curatedIcon = icon ?? resolveCuratedIcon(image);
    return (
        <Icon icon={curatedIcon} byType={iconByType} className={`imageIcon icon ${className}`} />
    );
};
